import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { getPublicEvent, publicEventRegister } from '../services/api';

// =====================================================
// Safely extract a string message from any error shape
// =====================================================
const extractErrorMessage = (err, fallback) => {
  if (!err) return fallback;

  const data = err.response?.data;

  if (typeof data === 'string') return data;
  if (data && typeof data.message === 'string') return data.message;
  if (data && data.message && typeof data.message.message === 'string') {
    return data.message.message;
  }
  if (data && typeof data.error === 'string') return data.error;
  if (data && data.error && typeof data.error.message === 'string') {
    return data.error.message;
  }
  if (typeof err.message === 'string') return err.message;

  return fallback;
};

const PublicEventRegister = () => {
  const { eventId } = useParams();

  // Storage key scoped to this event
  const STORAGE_KEY = `event-registration-${eventId}`;

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // ✅ Restore result from localStorage on initial render
  const [result, setResult] = useState(() => {
    try {
      const stored = localStorage.getItem(
        `event-registration-${eventId}`
      );
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getPublicEvent(eventId);
        setEvent(res.data);
      } catch (err) {
        setLoadError(extractErrorMessage(err, 'Event not found'));
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
    if (formError) setFormError('');
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError('');

      if (!formData.name.trim()) {
        return setFormError('Please enter your name');
      }
      if (!formData.email.trim()) {
        return setFormError('Please enter your email');
      }

      setSubmitting(true);
      try {
        const res = await publicEventRegister(eventId, {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
        });

        // ✅ Save to state
        setResult(res.data);

        // ✅ Persist so reload keeps showing success
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(res.data)
          );
        } catch (e) {
          console.warn(
            'Could not persist registration to localStorage:',
            e
          );
        }
      } catch (err) {
        setFormError(
          extractErrorMessage(err, 'Submission failed')
        );
      } finally {
        setSubmitting(false);
      }
    },
    [eventId, formData, STORAGE_KEY]
  );

  // =====================================================
  // Reset registration (Not you? Register again)
  // =====================================================
  const handleRegisterAgain = useCallback(() => {
    if (
      !window.confirm(
        'This will clear the saved registration on this device. Continue?'
      )
    ) {
      return;
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}

    setResult(null);
    setFormData({ name: '', email: '' });
    setFormError('');
  }, [STORAGE_KEY]);

  // =====================================================
  // PDF Download
  // =====================================================
  const handleDownloadPDF = useCallback(async () => {
    if (!result) return;

    setDownloading(true);

    try {
      const customId = result.attendee.customId;
      const eventName = result.event.name || 'Event';
      const location = result.event.location || '—';
      const attendeeName = result.attendee.name || '—';
      const attendeeEmail = result.attendee.email || '—';

      const fmtDate = (d) =>
        d
          ? new Date(d).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : '—';

      const startDate = fmtDate(result.event.startDate);
      const endDate = fmtDate(result.event.endDate);

      const qrDataUrl = await QRCode.toDataURL(customId, {
        width: 600,
        margin: 1,
        color: { dark: '#0F172A', light: '#FFFFFF' },
      });

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      doc.setFillColor(240, 244, 248);
      doc.rect(0, 0, pageW, pageH, 'F');

      const cardX = 12;
      const cardY = 12;
      const cardW = pageW - 24;
      const cardH = pageH - 24;

      doc.setFillColor(255, 255, 255);
      doc.roundedRect(cardX, cardY, cardW, cardH, 5, 5, 'F');

      const headerH = 34;

      doc.setFillColor(22, 114, 185);
      doc.roundedRect(cardX, cardY, cardW, headerH, 5, 5, 'F');
      doc.rect(cardX, cardY + 5, cardW, headerH - 5, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('NFCfied Access', cardX + 10, cardY + 15);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(220, 235, 250);
      doc.text('Digital Event Access', cardX + 10, cardY + 22);

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('EVENT PASS', cardX + cardW - 10, cardY + 20, {
        align: 'right',
      });

      let y = cardY + headerH + 14;

      doc.setTextColor(112, 128, 144);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('EVENT', cardX + 14, y);

      y += 8;
      doc.setTextColor(30, 41, 59);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      const nameLines = doc.splitTextToSize(eventName, cardW - 28);
      doc.text(nameLines, cardX + 14, y);
      y += nameLines.length * 9;

      y += 5;
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`${startDate}   –   ${endDate}`, cardX + 14, y);

      y += 6;
      doc.text(location, cardX + 14, y);

      y += 8;
      doc.setDrawColor(230, 235, 240);
      doc.setLineWidth(0.3);
      doc.line(cardX + 14, y, cardX + cardW - 14, y);

      y += 12;
      doc.setTextColor(112, 128, 144);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('ATTENDEE', cardX + 14, y);

      y += 8;
      const labelX = cardX + 14;
      const valueX = cardX + 48;
      const valueMaxW = cardW - 62;

      const rows = [
        { label: 'Name', value: attendeeName },
        { label: 'Email', value: attendeeEmail },
      ];

      for (const row of rows) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(112, 128, 144);
        doc.text(row.label, labelX, y);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(30, 41, 59);

        const valLines = doc.splitTextToSize(
          String(row.value),
          valueMaxW
        );
        doc.text(valLines, valueX, y);
        y += valLines.length * 5.5 + 5;
      }

      y += 3;
      doc.line(cardX + 14, y, cardX + cardW - 14, y);

      y += 14;
      const qrSize = 78;
      const qrX = (pageW - qrSize) / 2;
      const qrY = y;

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(220, 228, 236);
      doc.setLineWidth(0.5);
      doc.roundedRect(
        qrX - 6,
        qrY - 6,
        qrSize + 12,
        qrSize + 12,
        3,
        3,
        'FD'
      );

      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      y = qrY + qrSize + 12;
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text('Show this QR at the entrance', pageW / 2, y, {
        align: 'center',
      });

      const footerLineY = cardY + cardH - 18;

      doc.setDrawColor(230, 235, 240);
      doc.setLineWidth(0.3);
      doc.line(
        cardX + 14,
        footerLineY,
        cardX + cardW - 14,
        footerLineY
      );

      doc.setTextColor(150, 158, 170);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(
        `Generated on ${new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}`,
        pageW / 2,
        footerLineY + 7,
        { align: 'center' }
      );

      doc.text(
        'Keep this pass safe — it is required for entry',
        pageW / 2,
        footerLineY + 12,
        { align: 'center' }
      );

      doc.save(`event-pass-${customId}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloading(false);
    }
  }, [result]);

  // =====================================================
  // Loading
  // =====================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading event...</p>
        </div>
      </div>
    );
  }

  // =====================================================
  // Event not found
  // =====================================================
  if (loadError || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-red-100 shadow-sm p-8 max-w-md w-full text-center">
          <p className="text-red-600 font-semibold mb-2">
            {String(loadError || 'Event not found')}
          </p>
          <p className="text-sm text-gray-500">
            Please check the link or ask event staff for help.
          </p>
        </div>
      </div>
    );
  }

  // =====================================================
  // Success
  // =====================================================
  if (result) {
    const isQREvent = result.eventType === 'qr';
    const personalId = result.attendee?.customId;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              You're all set!
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {isQREvent
                ? 'Show this QR at the entrance'
                : 'Show this at the entrance desk'}
            </p>
          </div>

          {isQREvent && personalId ? (
            <>
              <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-center mb-4">
                <QRCodeCanvas value={personalId} size={220} />
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-gray-900">
                    {result.attendee.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium text-gray-900">
                    {result.attendee.email}
                  </span>
                </div>
              </div>

              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="w-full mt-4 bg-[#1672b9] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[#0f5a9e] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {downloading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3M4 4v16a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H6a2 2 0 00-2 2z"
                      />
                    </svg>
                    Download Event Pass (PDF)
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center mt-3">
                📸 You can also screenshot the QR above
              </p>

              <button
                onClick={handleRegisterAgain}
                className="w-full mt-3 text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Not you? Register a different attendee
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                {typeof result.message === 'string'
                  ? result.message
                  : 'Registration complete'}
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mt-6 text-left space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-gray-900">
                    {result.attendee.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium text-gray-900 truncate ml-2">
                    {result.attendee.email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Event</span>
                  <span className="font-medium text-gray-900 truncate ml-2">
                    {result.event.name}
                  </span>
                </div>
              </div>

              <p className="text-xs text-gray-400 text-center mt-6">
                Collect your badge at the entrance desk
              </p>

              <button
                onClick={handleRegisterAgain}
                className="w-full mt-3 text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Not you? Register a different attendee
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // =====================================================
  // Submission form
  // =====================================================
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-900">
            {event.name}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {event.location}
          </p>
          {event.startDate && (
            <p className="text-xs text-gray-400 mt-1">
              {new Date(event.startDate).toLocaleDateString()} —{' '}
              {new Date(event.endDate).toLocaleDateString()}
            </p>
          )}
        </div>

        {formError && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-100 text-red-700 text-sm">
            {String(formError)}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
              required
              disabled={submitting}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
              required
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1672b9] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[#0f5a9e] disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>

        <p className="text-xs text-gray-400 text-center mt-4">
          {event.eventType === 'qr'
            ? "You'll receive your QR code right after submitting"
            : 'Collect your badge at the entrance desk'}
        </p>
      </div>
    </div>
  );
};

export default PublicEventRegister;